import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Button } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';

function copyText(str) {
  const ta = document.createElement('textarea');
  ta.value = str;
  ta.setAttribute('readonly', '');
  ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } catch (_) {
    /* */
  }
  document.body.removeChild(ta);
  try {
    navigator.clipboard?.writeText(str);
  } catch (_) {
    /* */
  }
}

function CopyButton({ text, size = 'small', style = {} }) {
  const [copied, setCopied] = React.useState(false);
  const t = React.useRef(null);
  const go = () => {
    copyText(String(text || ''));
    setCopied(true);
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => setCopied(false), 2000);
  };
  React.useEffect(() => () => {
    if (t.current) clearTimeout(t.current);
  }, []);
  return (
    <Button
      type="text"
      size={size}
      icon={copied ? <CheckOutlined /> : <CopyOutlined />}
      onClick={go}
      style={{
        color: copied ? '#10b981' : '#6b7280',
        background: copied ? '#ecfdf5' : 'rgba(255,255,255,.9)',
        border: `1px solid ${copied ? '#d1fae5' : '#e5e7eb'}`,
        borderRadius: 8,
        transition: 'all 200ms ease',
        fontWeight: 500,
        fontSize: 13,
        ...style,
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

function InlineCopyIcon({ text }) {
  const [copied, setCopied] = React.useState(false);
  const t = React.useRef(null);
  const go = (e) => {
    e.stopPropagation();
    copyText(String(text || ''));
    setCopied(true);
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => setCopied(false), 2000);
  };
  React.useEffect(() => () => {
    if (t.current) clearTimeout(t.current);
  }, []);
  return (
    <span
      onClick={go}
      title="Copy"
      style={{
        cursor: 'pointer',
        marginLeft: 4,
        color: copied ? '#10b981' : '#9ca3af',
        fontSize: 12,
        transition: 'color 200ms ease',
        verticalAlign: 'middle',
      }}
    >
      {copied ? <CheckOutlined /> : <CopyOutlined />}
    </span>
  );
}

const mdComponents = {
  code({ inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || '');
    const codeString = String(children).replace(/\n$/, '');
    if (!inline && match) {
      return (
        <div className="chat-md-code-wrap">
          <div className="chat-md-code-actions">
            <CopyButton text={codeString} />
          </div>
          <SyntaxHighlighter
            style={prism}
            language={match[1]}
            PreTag="div"
            customStyle={{
              margin: 0,
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              background: '#f9fafb',
            }}
            {...props}
          >
            {codeString}
          </SyntaxHighlighter>
        </div>
      );
    }
    return (
      <span className="chat-md-inline-code-wrap">
        <code className={`chat-md-inline-code ${className || ''}`} {...props}>
          {children}
        </code>
        <InlineCopyIcon text={codeString} />
      </span>
    );
  },
  p: ({ children }) => <p className="chat-md-p">{children}</p>,
  ul: ({ children }) => <ul className="chat-md-ul">{children}</ul>,
  ol: ({ children }) => <ol className="chat-md-ol">{children}</ol>,
  li: ({ children }) => <li className="chat-md-li">{children}</li>,
  h1: ({ children }) => <h1 className="chat-md-h1">{children}</h1>,
  h2: ({ children }) => <h2 className="chat-md-h2">{children}</h2>,
  h3: ({ children }) => <h3 className="chat-md-h3">{children}</h3>,
  blockquote: ({ children }) => <blockquote className="chat-md-blockquote">{children}</blockquote>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="chat-md-a">
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="chat-md-table-scroll">
      <table className="chat-md-table">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="chat-md-th">{children}</th>,
  td: ({ children }) => <td className="chat-md-td">{children}</td>,
};

export default function AssistantMarkdown({ content }) {
  return (
    <div className="chat-md">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
        {content ?? ''}
      </ReactMarkdown>
    </div>
  );
}
